/**
 * Industry-standard datetime helpers
 *
 * Backend / DB: store & return UTC ISO 8601 only (e.g. 2026-07-31T21:18:51.911938+00:00)
 * Frontend: parse those UTC instants, then display in the viewer's device/browser timezone.
 *
 * Never format with a forced company/staff/server timezone for general display.
 * Do not persist the user's timezone unless a scheduling feature needs it.
 */

/** IANA timezone of the current device (e.g. "Asia/Karachi"). */
export function getDeviceTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Parse API/DB timestamps as UTC instants.
 * Naive ISO strings (no Z/offset) are treated as UTC.
 * Date-only YYYY-MM-DD is treated as a calendar date (local midnight) so the day does not shift.
 */
export function toUtcDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const raw = String(value).trim();
  if (!raw) return null;

  // Date-only: local calendar date (no UTC midnight → previous-day shift).
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split('-').map(Number);
    const local = new Date(y, m - 1, d);
    return Number.isNaN(local.getTime()) ? null : local;
  }

  let normalized = raw;
  if (
    /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(raw) &&
    !/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)
  ) {
    normalized = raw.replace(' ', 'T') + 'Z';
  }

  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format a UTC instant in the user's device local timezone.
 * Omitting `timeZone` lets Intl use the browser/OS timezone automatically.
 */
export function formatLocal(value, options = {}, fallback = '-') {
  const date = toUtcDate(value);
  if (!date) return fallback;
  try {
    return new Intl.DateTimeFormat('en-US', options).format(date);
  } catch {
    return fallback;
  }
}

/** Time — e.g. "10:30 AM" (device local) */
export function formatLocalTime(value, fallback = '-') {
  return formatLocal(
    value,
    { hour: 'numeric', minute: '2-digit', hour12: true },
    fallback
  );
}

/**
 * Format a UTC instant as wall-clock time in a specific IANA timezone
 * (e.g. staff.timezone on admin attendance). Falls back to device local
 * if timeZone is missing/invalid.
 */
export function formatTimeInTimeZone(value, timeZone, fallback = '-') {
  const date = toUtcDate(value);
  if (!date) return fallback;
  const tz = (timeZone || '').trim();
  try {
    const opts = { hour: 'numeric', minute: '2-digit', hour12: true };
    if (tz) opts.timeZone = tz;
    return new Intl.DateTimeFormat('en-US', opts).format(date);
  } catch {
    return formatLocalTime(value, fallback);
  }
}

/** Time with seconds — e.g. "10:30:45 AM" (device local) */
export function formatLocalTimeWithSeconds(value, fallback = '-') {
  return formatLocal(
    value,
    {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    },
    fallback
  );
}

/** Date — e.g. "Jan 29, 2026" (device local) */
export function formatLocalDate(value, fallback = '-') {
  return formatLocal(
    value,
    { year: 'numeric', month: 'short', day: 'numeric' },
    fallback
  );
}

/** Date + time — e.g. "Jan 29, 2026, 10:30 AM" (device local) */
export function formatLocalDateTime(value, fallback = '-') {
  return formatLocal(
    value,
    {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    },
    fallback
  );
}

/** YYYY-MM-DD in the device's local calendar */
export function formatLocalDateISO(value, fallback = '') {
  const date = toUtcDate(value);
  if (!date) return fallback;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Today's date (YYYY-MM-DD) in the device local timezone */
export function todayLocal() {
  return formatLocalDateISO(new Date());
}

/**
 * Offset (ms) of `timeZone` at the given UTC instant:
 * zonedWallClockAsUtc - actualUtc.
 */
function getTimeZoneOffsetMs(date, timeZone) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(date);
  const map = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  // hour "24" can appear at midnight in some engines
  const hour = Number(map.hour) % 24;
  const asUTC = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    hour,
    Number(map.minute),
    Number(map.second)
  );
  return asUTC - date.getTime();
}

/**
 * Convert a wall-clock HH:MM in `sourceTimeZone` (on that zone's "today")
 * to a UTC Date instant, then displayable via device-local formatters.
 */
export function shiftWallTimeToDate(timeStr, sourceTimeZone = 'UTC', refDate = new Date()) {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;

  const tz = sourceTimeZone || 'UTC';
  let y;
  let mo;
  let d;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(refDate);
    const map = {};
    for (const p of parts) {
      if (p.type !== 'literal') map[p.type] = p.value;
    }
    y = Number(map.year);
    mo = Number(map.month);
    d = Number(map.day);
  } catch {
    y = refDate.getUTCFullYear();
    mo = refDate.getUTCMonth() + 1;
    d = refDate.getUTCDate();
  }

  // Two-pass offset correction handles DST boundaries
  let utcMs = Date.UTC(y, mo - 1, d, hour, minute, 0);
  try {
    utcMs -= getTimeZoneOffsetMs(new Date(utcMs), tz);
    utcMs = Date.UTC(y, mo - 1, d, hour, minute, 0) - getTimeZoneOffsetMs(new Date(utcMs), tz);
  } catch {
    // invalid timezone — treat wall time as UTC
  }
  const result = new Date(utcMs);
  return Number.isNaN(result.getTime()) ? null : result;
}

/** Format shift HH:MM (in staff/company TZ) as device-local 12h time. */
export function formatShiftTimeLocal(timeStr, sourceTimeZone = 'UTC', fallback = '--:--') {
  const date = shiftWallTimeToDate(timeStr, sourceTimeZone);
  if (!date) {
    // Fallback: format HH:MM as-is in 12h without conversion
    if (!timeStr) return fallback;
    const [hStr, mStr] = String(timeStr).split(':');
    const hour = parseInt(hStr, 10);
    if (Number.isNaN(hour)) return fallback;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12.toString().padStart(2, '0')}:${mStr || '00'} ${ampm}`;
  }
  return formatLocalTime(date, fallback);
}

/** First day of the current local month (YYYY-MM-01) */
export function firstOfMonthLocal() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
}

/** @deprecated Prefer formatLocal — kept for gradual migration */
export const formatUtc = formatLocal;
/** @deprecated Prefer formatLocalTime */
export const formatUtcTime = formatLocalTime;
/** @deprecated Prefer formatLocalTimeWithSeconds */
export const formatUtcTimeWithSeconds = formatLocalTimeWithSeconds;
/** @deprecated Prefer formatLocalDate */
export const formatUtcDate = formatLocalDate;
/** @deprecated Prefer formatLocalDateTime */
export const formatUtcDateTime = formatLocalDateTime;
/** @deprecated Prefer formatLocalDateISO */
export const formatUtcDateISO = formatLocalDateISO;
/** @deprecated Prefer todayLocal */
export const todayUtc = todayLocal;
/** @deprecated Prefer firstOfMonthLocal */
export const firstOfMonthUtc = firstOfMonthLocal;

/**
 * Calendar "today" for filters. Uses device local timezone (not company TZ).
 * `timezone` arg ignored — kept for call-site compatibility.
 */
export function todayInTimezone(_timezone = 'UTC') {
  return todayLocal();
}

export function isTodayInTimezone(dateStr, _timezone = 'UTC') {
  return dateStr === todayLocal();
}
